import React, { useCallback, useState } from 'react';

import { InlineAlert } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { usePaymentContext } from 'features/payment';
import { initialErrorState } from 'features/payment/initial-state';
import { CardType } from 'features/payment/types';
import { mapPaymentMethodCardTypes, onCCFormChange } from 'features/payment/utils';
import { useIntl } from 'react-intl';

import PaymentMethodSelect, { HoverTheme } from 'components/payment-method-select';
import CreditCardFormInputs from 'experiments/checkout-v2/control/components/credit-card-form-inputs';
import { usePrepaidsPaymentMethods } from 'hooks/prepaid/use-prepaids-payment-methods';
import { emptyCartContext, useCartContext } from 'state/cart';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { SupportedCardType, defaultSupportedCardTypes } from 'state/launchdarkly/variations';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { InstructionText, LoadContainer, ReloadWrapper } from './styled';
import { IFullPrepaidMethodReloadProps } from './types';

const PrepaidMethodReload: React.FC<React.PropsWithChildren<IFullPrepaidMethodReloadProps>> = ({
  isDelivery,
  reloadAmount,
  reloadPaymentValues,
  setReloadPaymentValues,
}) => {
  const [errors, setErrors] = useState(initialErrorState);
  const { formatMessage } = useIntl();
  const { formatCurrencyForUser } = useUIContext();
  const { setPrepaidReloadPaymentMethodId } = usePaymentContext();
  const {
    nonSelectedReloadPaymentMethods,
    reloadPaymentMethods,
    selectedReloadPaymentMethod,
  } = usePrepaidsPaymentMethods();
  const router = useRouter();

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const formattedReloadAmount = formatCurrencyForUser(reloadAmount);

  const onChange = useCallback(
    (name: string, value: string) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        reloadPaymentValues,
        errors,
        formatMessage,
        supportedCardTypes
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setReloadPaymentValues(prevState => ({ ...prevState, ...state }));
    },
    [reloadPaymentValues, errors, formatMessage, setReloadPaymentValues, supportedCardTypes]
  );

  const reloadExplanationText = formatMessage(
    { id: 'reloadExplanation.bk' },
    { reloadAmount: formattedReloadAmount }
  );
  const youWillBeChargedText = formatMessage(
    { id: 'youWillBeCharged' },
    { reloadAmount: formattedReloadAmount }
  );
  const reloadExplanationAndChargedAmountText = `${reloadExplanationText} ${youWillBeChargedText}`;

  const isInCart = useCartContext() !== emptyCartContext;

  return (
    <ReloadWrapper>
      <InlineAlert message={reloadExplanationAndChargedAmountText} status="info" />
      <LoadContainer>
        <InstructionText>
          {formatMessage({ id: reloadPaymentMethods.length ? 'loadWith' : 'addCreditCard' })}:
        </InstructionText>
        {reloadPaymentMethods.length ? (
          <PaymentMethodSelect
            onOptionClick={setPrepaidReloadPaymentMethodId}
            nonSelectedMethods={nonSelectedReloadPaymentMethods}
            selectedMethod={selectedReloadPaymentMethod}
            hoverTheme={HoverTheme.LIGHT}
            onNavigatePress={() => router.navigate(`${routes.addCard}?isInCart=${isInCart}`)}
          />
        ) : (
          <CreditCardFormInputs
            ariaLabel="credit-card-form-inputs"
            isDelivery={isDelivery}
            onChange={onChange}
            paymentValues={reloadPaymentValues}
            errors={errors}
            withBorder={false}
          />
        )}
      </LoadContainer>
    </ReloadWrapper>
  );
};

export default PrepaidMethodReload;
